import { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import {
  GrcTextBox,
  GrcSwitch,
  FontAwesomeIcon,
  IconProp,
  fatruckclassicregular
} from '@grc/ui-package';
import { useGetApiData } from '@grc/ui-utility';
import { BusinessEntityType } from '@grc/ui-shared/lib/components/models/enums/enums';
import { useI18nState } from '../../../stores/settings/language-context';
import { fetchBusinessEntities } from '../../../service/api/fetch-business-entities';
import { useShipmentEntities } from '../useShipmentEntities';
import useShipmentHeatMapData, {
  addressToString,
  EtaStatus,
  IShipmentHeatMapPin
} from './useShipmentHeatMapData';
import { IShipment } from '../../../models/shipments/shipment.interface';
import { IDateRangeFilterValue } from '../../../common/date-range-filter/date-range-filter.interface';
import ShipmentPinDetailsPanel from './shipment-pin-details-panel';
import Loader from '../../../common/loader';

import './shipment-heat-map.scss';

interface IShipmentHeatMapProps {
  searchText: string;
  dateFilter: IDateRangeFilterValue;
  onShipmentIdClick: (shipment: IShipment) => void;
}

// Deliberately not '100%': a percentage height here depends on
// .shipment-heat-map-map having a *definite* computed height, and that
// element's own height is established via min-height (not height) to work
// around the devextreme tab-content wrapper chain (see shipment-heat-map.scss)
// - confirmed via logging that height:100% resolved to 0px in that
// situation. A literal pixel value, matching that same min-height floor,
// has no percentage-resolution step left to fail.
const mapContainerStyle = { width: '100%', height: '450px' };
const defaultCenter = { lat: 39.8283, lng: -98.5795 };
const defaultZoom = 4;
// The UI reference shows a single map view with no Map/Satellite toggle -
// only mapTypeControl is disabled; fullscreen/street-view/zoom controls stay
// (the reference shows those too).
const mapOptions: google.maps.MapOptions = { mapTypeControl: false };

// Marker fill colors read from the same status semantic tokens the rest of
// the app uses (_semantic.scss's --color-status-success-text/--color-status-danger-text) -
// Google Maps' Symbol API needs a literal color string, not a CSS var, so
// the token is resolved once at render time with a hex fallback.
function readStatusColor(varName: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
  return value || fallback;
}

function markerIcon(status: EtaStatus): google.maps.Symbol {
  const color =
    status === 'delay'
      ? readStatusColor('--color-status-danger-text', '#c62828')
      : readStatusColor('--color-status-success-text', '#2e7d32');

  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale: 8,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: '#ffffff',
    strokeWeight: 2
  };
}

// Shipment-centric heat map (see docs/shipment-tracking-map-api-gaps.md for
// the backend gaps this works around client-side): map + list view + a
// details panel, built entirely from existing services - no backend changes.
export default function ShipmentHeatMap({
  searchText,
  dateFilter,
  onShipmentIdClick
}: IShipmentHeatMapProps) {
  const { localeData } = useI18nState();
  const { fetchData } = useGetApiData();
  const { pins, isLoading } = useShipmentHeatMapData(searchText, dateFilter);
  // useShipmentEntities' effect depends on this array by reference - a
  // fresh `.map()` literal here would recreate it every render (even when
  // `pins` itself hasn't changed) and re-fire that effect in a tight loop
  // ("Maximum update depth exceeded"). Memoizing on `pins` keeps the
  // reference stable across renders that don't actually change the data.
  const shipmentsForEntities = useMemo(
    () => pins.map(pin => pin.shipment),
    [pins]
  );
  const { destinationsMap } = useShipmentEntities(shipmentsForEntities);
  const [originsMap, setOriginsMap] = useState<Record<string, any>>({});

  const [isListViewOpen, setIsListViewOpen] = useState(false);
  const [listSearch, setListSearch] = useState('');
  const [showOnTime, setShowOnTime] = useState(true);
  const [showDelay, setShowDelay] = useState(true);
  const [selectedPin, setSelectedPin] = useState<IShipmentHeatMapPin>();
  const [autoOpenTracking, setAutoOpenTracking] = useState(false);

  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY ?? ''
  });

  // Google Maps calls window.gm_authFailure on an invalid/restricted API
  // key (wrong referrer, Maps JavaScript API not enabled, etc.) - that
  // failure mode renders a blank/grey map with no markers and logs to the
  // console on its own, without throwing a catchable React error, so it's
  // otherwise invisible in an error boundary or React devtools.
  useEffect(() => {
    (window as any).gm_authFailure = () => {
      // eslint-disable-next-line no-console
      console.error(
        '[ShipmentHeatMap] Google Maps auth failure - check REACT_APP_GOOGLE_MAPS_KEY validity, API restrictions (Maps JavaScript API enabled?), and HTTP referrer allowlist.'
      );
    };
  }, []);

  useEffect(() => {
    const originIds = Array.from(
      new Set(pins.map(pin => pin.shipment.originId).filter(Boolean))
    );
    if (!originIds.length) {
      setOriginsMap({});
      return;
    }

    let isCancelled = false;
    fetchBusinessEntities({
      fetchData,
      entityIds: originIds,
      entityType: BusinessEntityType.Location
    }).then(result => {
      if (!isCancelled) setOriginsMap(result?.map ?? {});
    });

    return () => {
      isCancelled = true;
    };
  }, [pins, fetchData]);

  const filteredPins = useMemo(
    () =>
      pins.filter(pin => {
        if (pin.etaStatus === 'delay' && !showDelay) return false;
        if (pin.etaStatus === 'onTime' && !showOnTime) return false;
        if (!listSearch) return true;
        return pin.shipment.shipmentNumber
          .toLowerCase()
          .includes(listSearch.toLowerCase());
      }),
    [pins, showOnTime, showDelay, listSearch]
  );

  const pinsWithPosition = useMemo(
    () => filteredPins.filter(pin => !!pin.position),
    [filteredPins]
  );

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !pinsWithPosition.length) return;

    const bounds = new window.google.maps.LatLngBounds();
    pinsWithPosition.forEach(pin => {
      if (pin.position) bounds.extend(pin.position);
    });
    mapRef.current.fitBounds(bounds);
  }, [isLoaded, pinsWithPosition]);

  // Selecting a pin (map marker or list row) swaps the list panel for the
  // details panel in the same floating slot, matching the mockup's
  // list -> details navigation; "back" reverses that. `autoTrack` is set
  // when the card's own tracking-icon button (rather than the card body)
  // was clicked, so the details panel opens straight into Tracking Updates
  // instead of requiring a second click once it loads.
  const selectPin = (pin: IShipmentHeatMapPin, autoTrack = false) => {
    setSelectedPin(pin);
    setIsListViewOpen(false);
    setAutoOpenTracking(autoTrack);
  };

  const backToList = () => {
    setSelectedPin(undefined);
    setIsListViewOpen(true);
  };

  const resolveLocationText = (map: Record<string, any>, id: string) => {
    const entity = map?.[id];
    // Confirmed against a real /businessentities response: { id, name,
    // address, ... } - not the PascalCase EntityName field this used to
    // read (same fix as location-popover.tsx).
    const name = entity?.name ?? '';
    // entity.address is an IAddress object (street/city/state/zip/country),
    // not a pre-formatted string - confirmed by a "objects are not valid as
    // a React child" crash when rendering it directly. addressToString is
    // the same join logic already used for the geocoding fallback.
    const rawAddress = entity?.address;
    const address =
      typeof rawAddress === 'string' ? rawAddress : addressToString(rawAddress);
    return { name, address };
  };

  return (
    <div className="shipment-heat-map" data-testid="shipment_heat_map">
      <div className="shipment-heat-map-toolbar d-flex align-items-center">
        {!isListViewOpen && (
          <div className="shipment-heat-map-search position-relative">
            <GrcTextBox
              placeholder={localeData.SearchByShipmentId}
              value={listSearch}
              mode="search"
              onValueChange={(value: any) => setListSearch(value ?? '')}
            />
          </div>
        )}

        {!isListViewOpen && (
          <button
            type="button"
            className="grc-btn-secondary shipment-heat-map-list-toggle"
            onClick={() => setIsListViewOpen(open => !open)}
          >
            {localeData.ListView}
          </button>
        )}
      </div>


      <div className="shipment-heat-map-eta-toggle d-flex align-items-center">
        <div className="eta-toggle-item eta-toggle-on-time d-flex align-items-center">
          <GrcSwitch
            value={showOnTime}
            onValueChange={value => setShowOnTime(value)}
            elementAttr={{ 'aria-label': localeData.OnTimeDelivery }}
          />
          <span>{localeData.OnTimeDelivery}</span>
        </div>
        <div className="eta-toggle-item eta-toggle-delay d-flex align-items-center">
          <GrcSwitch
            value={showDelay}
            onValueChange={value => setShowDelay(value)}
            elementAttr={{ 'aria-label': localeData.DelayedDeliveries }}
          />
          <span>{localeData.DelayedDeliveries}</span>
        </div>
      </div>

      <div className="shipment-heat-map-body">
        {isListViewOpen && (
          <div className="shipment-heat-map-list-panel">
            <div className="list-panel-header d-flex align-items-center justify-content-between">
              <span className="grc-b">{localeData.ListView}</span>
              <button
                type="button"
                className="btn-close"
                aria-label={localeData.Close}
                onClick={() => setIsListViewOpen(false)}
              />
            </div>
            <div className="list-panel-search position-relative">
              <GrcTextBox
                placeholder={localeData.SearchByShipmentId}
                value={listSearch}
                mode="search"
                onValueChange={(value: any) => setListSearch(value ?? '')}
              />
            </div>
            <div className="list-panel-rows">
              {!isLoading && filteredPins.length === 0 && (
                <div className="list-panel-empty grc-text-light">
                  {localeData.NoDataToShow}
                </div>
              )}
              {filteredPins.map(pin => {
                const origin = resolveLocationText(
                  originsMap,
                  pin.shipment.originId
                );
                const destination = resolveLocationText(
                  destinationsMap,
                  pin.shipment.destinationId
                );

                return (
                  <div
                    key={pin.shipment.shipmentId}
                    role="button"
                    tabIndex={0}
                    className={`list-panel-row eta-${pin.etaStatus}`}
                    onClick={() => selectPin(pin)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') selectPin(pin);
                    }}
                  >
                    <div className="list-panel-row-header d-flex align-items-center justify-content-between">
                      <span className="d-flex align-items-center">
                        <span
                          className={`list-panel-row-dot eta-dot-${pin.etaStatus}`}
                        />
                        <span className="grc-text-link">
                          {pin.shipment.shipmentNumber}
                        </span>
                      </span>
                      <button
                        type="button"
                        className="list-panel-row-track-button"
                        aria-label={localeData.TrackingUpdates}
                        onClick={e => {
                          e.stopPropagation();
                          selectPin(pin, true);
                        }}
                      >
                        <FontAwesomeIcon
                          icon={fatruckclassicregular as IconProp}
                        />
                      </button>
                    </div>
                    <div
                      className={`list-panel-row-eta eta-text-${pin.etaStatus}`}
                    >
                      {pin.etaStatus === 'delay'
                        ? localeData.DelayInTransit
                        : localeData.EtaFinalDestinationOnTime}
                    </div>
                    <div className="list-panel-row-locations">
                      <div className="list-panel-row-location">
                        <span className="grc-text-light">
                          {localeData.Origin}
                        </span>
                        <div>{origin.name}</div>
                        {origin.address && <div>{origin.address}</div>}
                      </div>
                      <div className="list-panel-row-location">
                        <span className="grc-text-light">
                          {localeData.Destination}
                        </span>
                        <div>{destination.name}</div>
                        {destination.address && (
                          <div>{destination.address}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="shipment-heat-map-map">
          {loadError && (
            <div className="grc-empty-state-text">{localeData.Error}</div>
          )}
          {/* The map only waits on the Maps script itself (isLoaded) - it
              renders with zero pins just fine while shipment data is still
              loading or genuinely empty, rather than being hidden behind
              the shipment-data loading state. */}
          {!loadError && !isLoaded && <Loader />}
          {!loadError && isLoaded && (
            <>
              {isLoading && (
                <div className="shipment-heat-map-data-loading">
                  <Loader />
                </div>
              )}
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={defaultCenter}
                zoom={defaultZoom}
                options={mapOptions}
                onLoad={map => {
                  mapRef.current = map;
                }}
                onUnmount={() => {
                  mapRef.current = null;
                }}
              >
                {pinsWithPosition.map(pin => (
                  <Marker
                    key={pin.shipment.shipmentId}
                    position={pin.position as google.maps.LatLngLiteral}
                    icon={markerIcon(pin.etaStatus)}
                    title={pin.shipment.shipmentNumber}
                    onClick={() => selectPin(pin)}
                  />
                ))}
              </GoogleMap>
            </>
          )}
        </div>

        {selectedPin && (
          <ShipmentPinDetailsPanel
            shipmentNumber={selectedPin.shipment.shipmentNumber}
            transport={selectedPin.transport}
            autoOpenTracking={autoOpenTracking}
            onBack={backToList}
            onViewFullDetails={() => onShipmentIdClick(selectedPin.shipment)}
          />
        )}
      </div>
    </div>
  );
}
